import { Link, Navigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CheckCircle2, Shield } from 'lucide-react'
import { AfosLogo } from '../components/Logo'
import { RequestAccess } from '../components/RequestAccess'
import { OrbitField } from '../components/OrbitField'
import { useAuth } from '../lib/auth'

const PERKS = [
  'Product lifecycle: ideas, RICE, roadmaps, AI team LOE',
  'Jira + Linear sync with human publish gates',
  'Agent skills teams auto-provisioned per tenant project',
  'Ephemeral co-build for product, eng, and AI together',
  'Human gates on certify, merge, and deploy — never silent PROD',
]

export function SignUpPage() {
  const { user, loading } = useAuth()
  const [params] = useSearchParams()
  if (!loading && user) return <Navigate to="/app" replace />

  return (
    <div className="afos-shell relative min-h-screen">
      <OrbitField />
      <div className="relative z-10 mx-auto grid min-h-screen max-w-6xl lg:grid-cols-2">
        <section className="flex flex-col justify-between px-4 py-8 sm:px-8 lg:py-12">
          <Link to="/" className="w-fit">
            <AfosLogo size={34} />
          </Link>
          <div className="my-10 max-w-lg lg:my-0">
            <motion.h1
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-[family-name:var(--font-display)] text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl"
            >
              <span className="text-gradient">Start your product factory.</span>
            </motion.h1>
            <p className="mt-4 text-base text-[var(--color-mist)] sm:text-lg">
              Create a workspace, capture an idea or pull Jira/Linear, get AI LOE, and
              ship a human-gated POC with agent teams — product and engineering on one OS.
            </p>
            <ul className="mt-8 space-y-3">
              {PERKS.map((perk) => (
                <li key={perk} className="flex items-start gap-3 text-sm text-[var(--color-foam)] sm:text-base">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-cyan)]" />
                  <span>{perk}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="hidden text-xs text-[var(--color-mist)] lg:block">
            Early access: workspaces are opened by invitation.
          </p>
        </section>

        <section className="flex items-end px-4 pb-10 sm:px-8 lg:items-center lg:pb-0">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 }}
            className="glass neon-ring w-full rounded-[2rem] p-6 sm:p-8"
          >
            <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-[var(--color-line)] px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-[var(--color-mist)]">
              <Shield className="h-3.5 w-3.5 text-[var(--color-cyan)]" />
              Early access
            </div>
            <h2 className="font-[family-name:var(--font-display)] text-2xl font-extrabold sm:text-3xl">
              Request early access
            </h2>
            <p className="mt-2 text-sm text-[var(--color-mist)]">
              AFOS 360 is invite only while we onboard early teams. Tell us who you are and a
              platform admin will email you an invite.
            </p>
            <div className="mt-8 space-y-4">
              <RequestAccess initialEmail={params.get('email') || ''} />
            </div>
            <p className="mt-6 text-center text-sm text-[var(--color-mist)]">
              Already invited?{' '}
              <Link to="/signin" className="text-[var(--color-cyan)] hover:underline">
                Sign in
              </Link>
            </p>
          </motion.div>
        </section>
      </div>
    </div>
  )
}
